import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Swords, Send, Loader2, Trophy, Target, AlertTriangle, CheckCircle,
  Lightbulb, ArrowRight, User, Bot, ThumbsUp, ThumbsDown, RotateCcw, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NegotiationSimulatorView = ({ contracts }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(true);
  const [finalScore, setFinalScore] = useState(null);
  const chatRef = useRef(null);
  const [form, setForm] = useState({
    project_name: "", framework: "FIDIC", your_role: "contractor",
    counterpart_role: "employer", contract_value: "", difficulty: "moderate", key_issues: ""
  });

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, [activeSession?.negotiation_history]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/negotiation/sessions`);
      setSessions(res.data.sessions || []);
    } catch (e) { /* */ }
  };

  const startSession = async () => {
    if (!form.project_name) { toast.error("Enter a project name"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        contract_value: parseFloat(form.contract_value) || null,
        key_issues: form.key_issues ? form.key_issues.split(",").map(s => s.trim()) : []
      };
      if (form.contract_id) payload.contract_id = form.contract_id;
      const res = await axios.post(`${API}/negotiation/sessions`, payload);
      setActiveSession(res.data);
      setShowSetup(false);
      setFinalScore(null);
      fetchSessions();
      toast.success("Negotiation session started!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start session"); }
    finally { setLoading(false); }
  };

  const sendMove = async () => {
    if (!message.trim() || !activeSession) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/negotiation/move`, {
        session_id: activeSession.id, message
      });
      setActiveSession(res.data.session);
      setMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setLoading(false); }
  };

  const endSession = async () => {
    if (!activeSession) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/negotiation/sessions/${activeSession.id}/end`);
      setFinalScore(res.data.final_score);
      toast.success("Session ended! Check your score.");
      fetchSessions();
    } catch (e) { toast.error("Failed to end session"); }
    finally { setLoading(false); }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/negotiation/sessions/${sessionId}`);
      setActiveSession(res.data);
      setShowSetup(false);
      setFinalScore(res.data.final_score || null);
    } catch (e) { toast.error("Failed to load session"); }
  };

  return (
    <div className="space-y-6" data-testid="negotiation-simulator-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Negotiation Simulator</h1>
          <p className="text-zinc-500 mt-1">Practice contract negotiations with an AI counterpart</p>
        </div>
        <div className="flex gap-2">
          {activeSession && activeSession.status === "active" && (
            <Button onClick={endSession} disabled={loading} variant="outline" className="border-2 border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-end-session">
              <Trophy size={16} className="mr-2" /> End & Score
            </Button>
          )}
          <Button onClick={() => { setShowSetup(true); setActiveSession(null); setFinalScore(null); }} className="bg-black text-white" data-testid="btn-new-session">
            <Swords size={16} className="mr-2" /> New Session
          </Button>
        </div>
      </div>

      {showSetup && (
        <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-4">
          <h2 className="text-lg font-bold font-['Chivo']">Setup Negotiation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Project Name</label>
              <input value={form.project_name} onChange={e => setForm(p => ({ ...p, project_name: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., Marina Bay Tower" data-testid="neg-project-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework</label>
              <select value={form.framework} onChange={e => setForm(p => ({ ...p, framework: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="neg-framework">
                {["FIDIC","NEC","JCT","ICC","AIA","EJCDC"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract Value ($)</label>
              <input type="number" value={form.contract_value} onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="50000000" data-testid="neg-value" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Your Role</label>
              <select value={form.your_role} onChange={e => setForm(p => ({ ...p, your_role: e.target.value, counterpart_role: e.target.value === "contractor" ? "employer" : "contractor" }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="neg-role">
                <option value="contractor">Contractor</option>
                <option value="employer">Employer</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm(p => ({ ...p, difficulty: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="neg-difficulty">
                <option value="easy">Easy</option>
                <option value="moderate">Moderate</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Key Issues (comma separated)</label>
              <input value={form.key_issues} onChange={e => setForm(p => ({ ...p, key_issues: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., LDs, payment terms" data-testid="neg-issues" />
            </div>
          </div>
          <Button onClick={startSession} disabled={loading || !form.project_name} className="bg-[#FF4F00] text-white px-8" data-testid="btn-start-negotiation">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Swords size={16} className="mr-2" />} Start Negotiation
          </Button>

          {sessions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Previous Sessions</h3>
              <div className="space-y-2">
                {sessions.slice(0, 5).map((s, i) => (
                  <div key={i} className="p-3 border border-zinc-200 hover:bg-zinc-50 cursor-pointer flex items-center justify-between"
                    onClick={() => loadSession(s.id)} data-testid={`session-${i}`}>
                    <div>
                      <p className="text-sm font-medium">{s.project_name}</p>
                      <p className="text-xs text-zinc-500">{s.framework} | {s.your_role} vs {s.counterpart_role} | {s.difficulty}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={s.status === "active" ? "bg-blue-100 text-blue-700 border-none" : "bg-zinc-100 text-zinc-700 border-none"}>{s.status}</Badge>
                      <ArrowRight size={14} className="text-zinc-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeSession && !showSetup && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 border-2 border-black brutal-shadow bg-white flex flex-col" style={{ height: "600px" }}>
            <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm">{activeSession.project_name}</h3>
                <p className="text-xs text-zinc-500">
                  {activeSession.framework} | You: {activeSession.your_role} vs AI: {activeSession.counterpart_role} | {activeSession.difficulty}
                </p>
              </div>
              <Badge className={activeSession.status === "active" ? "bg-emerald-100 text-emerald-700 border-none" : "bg-zinc-100 text-zinc-700 border-none"}>
                {activeSession.status}
              </Badge>
            </div>

            <ScrollArea className="flex-1 p-4" ref={chatRef}>
              <div className="space-y-4">
                {(activeSession.negotiation_history || []).map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === activeSession.your_role ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-4 ${
                      msg.role === "system" ? 'bg-zinc-100 border border-zinc-200 text-center w-full text-xs' :
                      msg.role === activeSession.your_role ? 'bg-[#0047AB] text-white' :
                      'bg-zinc-100 border border-zinc-200'
                    }`}>
                      {msg.role !== "system" && (
                        <div className="flex items-center gap-2 mb-2">
                          {msg.role === activeSession.your_role ? <User size={14} /> : <Bot size={14} />}
                          <span className="text-xs font-bold uppercase">{msg.role}</span>
                          {msg.stance && <Badge variant="outline" className={`text-xs ${
                            msg.stance === "accept" ? "border-emerald-300 text-emerald-700" :
                            msg.stance === "reject" ? "border-red-300 text-red-700" :
                            "border-amber-300 text-amber-700"
                          }`}>{msg.stance}</Badge>}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.counter_proposal && (
                        <div className="mt-2 pt-2 border-t border-zinc-300/30 text-xs">
                          <strong>Counter:</strong> {msg.counter_proposal}
                        </div>
                      )}
                      {msg.negotiation_tips && msg.negotiation_tips.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-300/30">
                          {msg.negotiation_tips.map((tip, j) => (
                            <div key={j} className="flex items-start gap-1 text-xs mt-1">
                              <Lightbulb size={10} className="mt-0.5 flex-shrink-0" style={{ color: msg.role === activeSession.your_role ? "#fff" : "#f59e0b" }} />
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="p-4 bg-zinc-100 border border-zinc-200">
                      <Loader2 size={16} className="animate-spin text-zinc-500" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {activeSession.status === "active" && (
              <div className="p-4 border-t border-zinc-200">
                <div className="flex gap-2">
                  <input value={message} onChange={e => setMessage(e.target.value)} onKeyPress={e => e.key === "Enter" && !loading && sendMove()}
                    placeholder="Make your negotiation move..." className="flex-1 border-2 border-zinc-200 px-4 py-3 text-sm" data-testid="neg-message-input" />
                  <Button onClick={sendMove} disabled={loading || !message.trim()} className="bg-[#FF4F00] text-white px-6" data-testid="btn-send-move">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="border-2 border-black brutal-shadow bg-white p-4">
              <h4 className="text-xs font-bold uppercase text-zinc-500 mb-3">Score</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Your Gains</span><span className="font-bold text-emerald-600">{activeSession.score?.your_gains || 0}</span></div>
                <div className="flex justify-between"><span>Their Gains</span><span className="font-bold text-red-600">{activeSession.score?.counterpart_gains || 0}</span></div>
                <div className="flex justify-between"><span>Compromises</span><span className="font-bold text-[#0047AB]">{activeSession.score?.compromises || 0}</span></div>
              </div>
            </div>

            <div className="border-2 border-zinc-200 bg-white p-4">
              <h4 className="text-xs font-bold uppercase text-zinc-500 mb-3">Clauses to Negotiate</h4>
              <div className="space-y-2">
                {(activeSession.clauses || []).map((c, i) => (
                  <div key={i} className="text-xs p-2 border border-zinc-100 hover:bg-zinc-50">
                    <p className="font-medium">{c.title}</p>
                    <p className="text-zinc-500 mt-0.5">{c.current}</p>
                    <Badge className={`mt-1 text-xs border-none ${c.risk_level === 'critical' ? 'bg-red-100 text-red-700' : c.risk_level === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>{c.risk_level}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {finalScore && (
        <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-4" data-testid="final-score-card">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold font-['Chivo']">Final Score</h2>
            <div className="flex items-center gap-3">
              <span className={`text-4xl font-black ${(finalScore.overall_score || 0) >= 70 ? 'text-emerald-600' : (finalScore.overall_score || 0) >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {finalScore.overall_score || 0}/100
              </span>
              <Badge className="text-lg px-3 py-1 bg-[#0047AB] text-white border-none">{finalScore.grade || "C"}</Badge>
            </div>
          </div>
          {finalScore.strengths?.length > 0 && (
            <div><h4 className="text-xs font-bold uppercase text-emerald-600 mb-2">Strengths</h4>
              {finalScore.strengths.map((s, i) => <div key={i} className="flex items-start gap-2 text-sm mb-1"><ThumbsUp size={14} className="text-emerald-500 mt-0.5" /><span>{s}</span></div>)}
            </div>
          )}
          {finalScore.weaknesses?.length > 0 && (
            <div><h4 className="text-xs font-bold uppercase text-red-600 mb-2">Areas to Improve</h4>
              {finalScore.weaknesses.map((w, i) => <div key={i} className="flex items-start gap-2 text-sm mb-1"><AlertTriangle size={14} className="text-red-500 mt-0.5" /><span>{w}</span></div>)}
            </div>
          )}
          {finalScore.learning_points?.length > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200">
              <h4 className="text-xs font-bold uppercase text-blue-600 mb-2">Key Takeaways</h4>
              {finalScore.learning_points.map((l, i) => <div key={i} className="flex items-start gap-2 text-sm mb-1"><Lightbulb size={14} className="text-amber-500 mt-0.5" /><span>{l}</span></div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NegotiationSimulatorView;
